/**
 * L'état d'interface de « Ma dispo » : le brouillon de la semaine et la boîte ouverte. Pur, testé.
 *
 * Port de `MaDispoScreen.js` :
 *  - toucher une heure libre la propose, une heure proposée la retire — sans confirmation, le
 *    brouillon se défait d'un toucher ; la PREMIÈRE heure posée du passage ouvre « Un créneau
 *    régulier, c'est un engagement », en accusé ;
 *  - toucher une heure avec un live ouvre « Créneau réservé à un live » ; une heure libérée est inerte ;
 *  - choisir une autre date de démarrage ouvre sa boîte.
 * `draft` à `null` suit la semaine servie : un brouillon n'existe qu'une fois une heure touchée.
 */
import { pressTutorHour } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import { type UITutorHour } from "../../../types/tutorDispoOffres"

export type TutorAvailabilityOpenDialog =
    | { kind: "startingDate"; date: string }
    | { kind: "continuity"; mode: "acknowledge" | "confirm" }
    | { kind: "leave" }
    | { kind: "live"; slotId: string; liveTimesLabel?: string }

export type TutorAvailabilityUiState = {
    draft: string[] | null
    dialog: TutorAvailabilityOpenDialog | null
    /** L'engagement a été montré à la première heure posée de ce passage. */
    continuityShown: boolean
    /** Un envoi refusé, dit dans une boîte (l'ancien `useApiStatus`). */
    errorMessage: string | null
}

export type TutorAvailabilityUiAction =
    | { type: "PRESS_HOUR"; hour: UITutorHour; serverOpenSlots: string[] }
    | { type: "PICK_STARTING_DATE"; date: string; current: string | null }
    | { type: "OPEN_DIALOG"; dialog: TutorAvailabilityOpenDialog }
    | { type: "CLOSE_DIALOG" }
    /** La semaine servie fait foi de nouveau : elle vient d'arriver, ou d'être enregistrée. */
    | { type: "RESEED" }
    | { type: "SHOW_ERROR"; message: string }
    | { type: "DISMISS_ERROR" }

export const initialTutorAvailabilityUiState: TutorAvailabilityUiState = {
    draft: null,
    dialog: null,
    continuityShown: false,
    errorMessage: null,
}

export function tutorAvailabilityUiReducer(
    state: TutorAvailabilityUiState,
    action: TutorAvailabilityUiAction
): TutorAvailabilityUiState {
    switch (action.type) {
        case "PRESS_HOUR": {
            const { hour, serverOpenSlots } = action
            const { openSlots, effect } = pressTutorHour(state.draft ?? serverOpenSlots, hour)
            if (effect === "none") return state
            if (effect === "live") {
                return { ...state, dialog: { kind: "live", slotId: hour.id, liveTimesLabel: hour.liveTimesLabel } }
            }
            if (effect === "removed" || state.continuityShown) return { ...state, draft: openSlots }
            return {
                ...state,
                draft: openSlots,
                continuityShown: true,
                dialog: { kind: "continuity", mode: "acknowledge" },
            }
        }
        case "PICK_STARTING_DATE":
            if (!action.date || action.date === action.current) return state
            return { ...state, dialog: { kind: "startingDate", date: action.date } }
        case "OPEN_DIALOG":
            return { ...state, dialog: action.dialog }
        case "CLOSE_DIALOG":
            return state.dialog == null ? state : { ...state, dialog: null }
        case "RESEED":
            return state.draft == null ? state : { ...state, draft: null }
        case "SHOW_ERROR":
            return { ...state, errorMessage: action.message }
        case "DISMISS_ERROR":
            return { ...state, errorMessage: null }
        default:
            return state
    }
}
